import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, Clock, FileText, Shield } from 'lucide-react';
import { ScrollReveal, StaggerItem, staggerContainer } from './ScrollAnimation';
import { motion } from 'framer-motion';

const InfoSections = () => {
  const { t, language } = useLanguage();

  // Use the French video for all languages
  const videoSrc = '/how-it-works-video-fr.mp4';

  // Set video start time when loaded
  const handleVideoLoad = (e) => {
    const video = e.target;
    video.currentTime = 1; // Start at 1 second to show the woman at the mic
    video.muted = true; // Force muted
  };

  // Prevent unmuting the video
  const handleVolumeChange = (e) => {
    const video = e.target;
    if (!video.muted) {
      video.muted = true; // Force it back to muted
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-orange-100 via-pink-100 to-rose-200">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* How It Works */}
        <div className="mb-12">
          <ScrollReveal variant="fadeInUp">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-12">
              {t('howItWorks.title').replace('?', '').split(' ').map((word, i, arr) => {
                const highlight = ['Works', 'marche', 'funciona', 'funziona'];
                return highlight.includes(word) ? 
                  <span key={i} className="bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">{word}</span> : 
                  (i === arr.length - 1 ? word + '?' : word + ' ');
              })}
            </h2>
          </ScrollReveal>
          <motion.div 
            className="grid md:grid-cols-3 gap-8 mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <StaggerItem index={0}>
              <Card className="glass-card border-2 border-white/50 hover:border-purple-300 transition-all hover:shadow-xl transform hover:scale-105 duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-500 via-violet-400 to-fuchsia-400 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {t('howItWorks.step1')}
                </h3>
                <p className="text-gray-700">
                  {t('howItWorks.step1Desc')}
                </p>
              </CardContent>
            </Card>
            </StaggerItem>

            <StaggerItem index={1}>
              <Card className="glass-card border-2 border-white/50 hover:border-cyan-300 transition-all hover:shadow-xl transform hover:scale-105 duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-teal-400 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {t('howItWorks.step2')}
                </h3>
                <p className="text-gray-700">
                  {t('howItWorks.step2Desc')}
                </p>
              </CardContent>
            </Card>
            </StaggerItem>

            <StaggerItem index={2}>
              <Card className="glass-card border-2 border-white/50 hover:border-teal-300 transition-all hover:shadow-xl transform hover:scale-105 duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-teal-500 to-emerald-400 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {t('howItWorks.step3')}
                </h3>
                <p className="text-gray-700">
                  {t('howItWorks.step3Desc')}
                </p>
              </CardContent>
            </Card>
            </StaggerItem>
          </motion.div>

          {/* Video Frame - Landscape with rounded corners */}
          <ScrollReveal variant="scaleIn" delay={0.4}>
          <div className="max-w-4xl mx-auto mt-12">
            <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50">
              <video
                controls
                muted
                className="w-full h-full object-contain"
                poster="https://customer-assets.emergentagent.com/job_compose-translate/artifacts/y073bwaz_image.png"
                onLoadedMetadata={handleVideoLoad}
                onVolumeChange={handleVolumeChange}
                key={videoSrc}
              >
                <source src={videoSrc} type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
          </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default InfoSections;
